import {
  Slide,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Checkbox,
} from "@mui/material";
import { Close, HelpOutline } from "@mui/icons-material";
import React, { useEffect, useState } from "react";

export default function OtaEditModel({ open, onClose, onSave, rowData }) {
  const fileInputRef = React.useRef(null);
  const [uploadType, setUploadType] = useState("binary");
  const [autoChecksum, setAutoChecksum] = useState(false);
  const [file, setFile] = useState(null);

  const initialFormState = {
    title: "",
    version: "",
    versionTag: "",
    deviceProfile: "",
    packageType: "",
    description: "",
    url: "",
    algorithm: "SHA-256",
    checksum: "",
  };
  const [formData, setFormData] = useState(initialFormState);
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

 useEffect(() => {
    if (open && rowData) {
      setFormData({
        title: rowData.title ?? "",
        version: rowData.version ?? "",
        versionTag: rowData.version_tag ?? "",
        packageType: rowData.package_type ?? "",
        url: rowData.url ?? "",
        checksum: rowData.checksum ?? "",
        description: rowData.description ?? ""
      });
    }
  }, [open]);

  const handleSubmit = () => {
    const payload = {
      id: rowData.id,
      title: formData.title,
      version: formData.version,
      version_tag: formData.versionTag,
      package_type: formData.packageType,
      url: formData.url,
      file_name: file?.name || rowData.file_name,
      file_size: file
        ? `${(file.size / 1024).toFixed(2)} KB`
        : rowData.file_size,
      checksum: autoChecksum ? "auto" : formData.checksum,
      description: formData.description
    };

    onSave(payload);
    onClose();
  };

  const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        // TransitionComponent={Transition}
        keepMounted
        fullWidth
        maxWidth="md"
      >
        {/* ===== Header ===== */}
        <DialogTitle
          sx={{
            backgroundColor: "#488de7",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Update package
          <Box>
            {/* <IconButton sx={{ color: "#fff" }}>
                      <HelpOutline />
                    </IconButton> */}
            <IconButton
              onClick={() => {
                setFormData(initialFormState);
                onClose();
              }}
              sx={{ color: "#fff" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* ===== Content ===== */}
        <DialogContent>
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Title + Version */}
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="Title*"
                fullWidth
                value={formData.title}
                onChange={handleChange("title")}
              />
              <TextField
                label="Version*"
                fullWidth
                value={formData.version}
                onChange={handleChange("version")}
              />
            </Box>

            {/* Version tag */}
            <Box>
              <TextField
                label="Version tag"
                fullWidth
                value={formData.versionTag}
                onChange={handleChange("versionTag")}
              />
              <Typography variant="caption" color="text.secondary">
                Custom tag should match the package version reported by your
                device.
              </Typography>
            </Box>

            {/* Device profile */}
            <TextField
              label="Device profile*"
              fullWidth
              value={formData.deviceProfile}
              onChange={handleChange("deviceProfile")}
            />

            {/* Package type */}
            <Select
              fullWidth
              value={formData.packageType}
              onChange={handleChange("packageType")}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select package type
              </MenuItem>
              <MenuItem value="Firmware">Firmware</MenuItem>
              <MenuItem value="Software">Software</MenuItem>
            </Select>

            {/* Upload type */}
            <RadioGroup
              row
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value)}
            >
              <FormControlLabel
                value="binary"
                control={<Radio />}
                label="Upload binary file"
              />
              <FormControlLabel
                value="url"
                control={<Radio />}
                label="Use external URL"
              />
            </RadioGroup>

            {/* File upload */}
            {uploadType === "binary" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="caption">Package file*</Typography>

                <Box
                  component="label"
                  sx={{
                    position: "relative",
                    border: "2px dashed #cfd8dc",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f9fafb" },
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      if (selectedFile) {
                        setFile(selectedFile);
                      }
                    }}
                  />

                  {/* Close icon */}
                  {file && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = ""; 
                        }
                      }}
                      sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  )}

                  <Typography color="text.secondary">
                    {file ? (
                      file.name
                    ) : (
                      <>
                        Drag and drop a package file or{" "}
                        <Box
                          component="span"
                          sx={{ color: "#00695c", fontWeight: 600 }}
                        >
                          Browse file
                        </Box>
                      </>
                    )}
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {file ? "File selected" : "No file selected"}
                </Typography>
              </Box>
            )}

            {/* URL upload */}
            {uploadType === "url" && (
              <TextField
                label="Package URL*"
                fullWidth
                value={formData.url}
                onChange={handleChange("url")}
              />
            )}

            {/* Auto checksum */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoChecksum}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAutoChecksum(checked);

                    if (checked) {
                      setFormData((prev) => ({
                        ...prev,
                        checksum: "",
                      }));
                    }
                  }}
                />
              }
              label="Auto-generate checksum"
            />

            {/* Manual checksum */}
            {!autoChecksum && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Checksum algorithm"
                  value={formData.algorithm}
                  onChange={handleChange("algorithm")}
                >
                  <MenuItem value="SHA-256">SHA-256</MenuItem>
                  <MenuItem value="SHA-1">SHA-1</MenuItem>
                  <MenuItem value="MD5">MD5</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  size="small"
                  label="Checksum"
                  value={formData.checksum}
                  onChange={handleChange("checksum")}
                  helperText="If empty, it will be generated automatically"
                />
              </Box>
            )}

            {/* Description */}
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange("description")}
            />
          </Box>
        </DialogContent>

        {/* ===== Actions ===== */}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setFormData(initialFormState);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
